import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { post } from './client';
import type {
  MedicationDose,
  MedicationHisOriginal,
  MedicationMealRelation,
} from './model';
import { patientKeys } from './patient';

// region 类型

export type MedicationAddedBy = 'doctor' | 'patient';
export type MedicationOrigin = 'his' | 'manual';
export type MedicationReviewStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | '';

// 一条可加入用药清单的 HIS 处方: 原始字段加上按频次换算的建议剂次
export interface MedicationHisCandidate {
  hisRxId: string;
  genericName: string;
  route: string;
  dosage: string;
  dosageUnit: string;
  specification: string;
  frequency: string;
  // YYYY-MM-DD
  startDate: string;
  endDate: string;
  days: number;
  fingerprint: string;
  suggestedDoses: MedicationDose[];
  mealRelation: MedicationMealRelation | '';
}

// 患者用药清单的一行, 用药管理的唯一真源
//
// addedBy 是谁添加: doctor 医生在后台添加 (专科医生批准后患者才看得到) / patient 患者在小程序添加 (即时生效, 后台只读)
// origin 是数据从哪来: his 从 HIS 处方加入 (名称 / 规格 / 途径 / 开始日期锁定) / manual 手填
export interface PatientMedicationItem {
  id: string;
  // 兼容字段, 由 addedBy 派生: plan 医生添加 / patient 患者添加
  source: 'plan' | 'patient';
  addedBy: MedicationAddedBy;
  origin: MedicationOrigin;
  hisRxId: string;
  medicationId: string;
  drugName: string;
  specification: string;
  route: string;
  dosage: string;
  dosageUnit: string;
  // 频次编码: HIS 来源为 HIS 频次, 手填的可为空 (由剂次个数表达)
  frequency: string;
  mealRelation: MedicationMealRelation | '';
  // 服药安排: 一天吃几次、几点吃、每次吃多少; 空数组表示尚未安排
  doses: MedicationDose[];
  reminderTimes: string[];
  // YYYY-MM-DD
  startDate: string;
  // YYYY-MM-DD, 空表示长期
  endDate: string;
  // 由起止日期算出的用药天数, 长期为 0
  days: number;
  // 提醒开关, 医生与患者共用一个值
  reminderEnabled: boolean;
  // 医生添加的行的审核状态; 患者添加的行为空
  reviewStatus: MedicationReviewStatus;
  reviewedBy: string;
  reviewedAt: string;
  // origin=his 时可微调字段的 HIS 原值快照
  hisOriginal: MedicationHisOriginal | null;
  adjustedBy: string;
  adjustedAt: string;
  // 归档 (停用) 时间, 空表示在用
  archivedAt: string;
  createdAt: string;
  updatedAt: string;
  // origin=his 的在用行与最新 HIS 处方不一致, 医生可按新处方重置; hisLatest 是最新的 HIS 值
  hisChanged: boolean;
  hisLatest: MedicationHisCandidate | null;
}

export interface PatientMedicationOverview {
  // 医生用药管理开关, 关闭时后台整体只读
  hisRxEnabled: boolean;
  // 患者的全部用药行 (含待审核、已拒绝、已归档), 归档的排最后
  items: PatientMedicationItem[];
  // 尚未加入清单的 HIS 处方 (开关关闭时为空)
  candidates: MedicationHisCandidate[];
}

// 医生手填或修改用药时提交的字段
export interface MedicationFieldsInput {
  drugName: string;
  specification: string;
  route: string;
  dosage: string;
  dosageUnit: string;
  frequency: string;
  mealRelation: MedicationMealRelation | '';
  doses: MedicationDose[];
  startDate: string;
  // 空表示长期
  endDate: string;
}

// endregion

// region 请求

export const patientMedicationKeys = {
  all: ['patient-medication'] as const,
  list: (accountId: string) =>
    [...patientMedicationKeys.all, 'list', accountId] as const,
};

export const getPatientMedicationOverview = (accountId: string) =>
  post<PatientMedicationOverview>('/patient/medication/list', { accountId });

// endregion

// region hooks

// 后台用药模块的数据: 方案编辑页与患者详情共用, 全部后台账号可读
export const usePatientMedicationListQuery = (accountId: string) =>
  useQuery({
    queryKey: patientMedicationKeys.list(accountId),
    queryFn: () => getPatientMedicationOverview(accountId),
    enabled: !!accountId,
  });

// 写接口都要求专科医生、方案负责人且开关打开; planId 用来定位患者并沿用方案的负责人口径
// 成功后整体重新拉清单, 患者详情的方案卡片也跟着刷新
function useMedicationMutation<TInput>(accountId: string, path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      post<PatientMedicationItem | null>(`/patient/medication/${path}`, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: patientMedicationKeys.list(accountId),
        }),
        queryClient.invalidateQueries({ queryKey: patientKeys.all }),
      ]),
  });
}

export const useMedicationAddFromHisMutation = (accountId: string) =>
  useMedicationMutation<{ planId: string; hisRxId: string }>(
    accountId,
    'add-from-his',
  );

export const useMedicationAddMutation = (accountId: string) =>
  useMedicationMutation<{ planId: string } & MedicationFieldsInput>(
    accountId,
    'add',
  );

export const useMedicationSaveMutation = (accountId: string) =>
  useMedicationMutation<
    {
      planId: string;
      id: string;
      reminderEnabled: boolean;
      reviewStatus: Exclude<MedicationReviewStatus, ''>;
    } & MedicationFieldsInput
  >(accountId, 'save');

export const useMedicationResetHisMutation = (accountId: string) =>
  useMedicationMutation<{ planId: string; id: string }>(accountId, 'reset-his');

export const useMedicationArchiveMutation = (accountId: string) =>
  useMedicationMutation<{ planId: string; id: string }>(accountId, 'archive');

export const useMedicationRestoreMutation = (accountId: string) =>
  useMedicationMutation<{ planId: string; id: string }>(accountId, 'restore');

export const useMedicationReorderMutation = (accountId: string) =>
  useMedicationMutation<{ planId: string; ids: string[] }>(
    accountId,
    'reorder',
  );

// endregion
